import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { groupSessionService } from '../api/services';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Video, Mic, MessageCircle, Users, ArrowLeft, Copy,
  Calendar, Clock, CheckCircle2, Share2, Loader2,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

const SESSION_ICON = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };
const STATUS_STYLE = {
  SCHEDULED: 'bg-amber-50 text-amber-700 border-amber-200',
  LIVE:      'bg-green-50 text-green-700 border-green-200',
  COMPLETED: 'bg-forest-50 text-forest-700 border-forest-200',
  CANCELLED: 'bg-cream-100 text-forest-400 border-cream-200',
};

const GroupSessionDetailPage = () => {
  const { id }       = useParams();
  const { user }     = useAuth();
  const navigate     = useNavigate();
  const [copied, setCopied] = useState(false);
  const [completing, setCompleting] = useState(false);

  const { data, loading, error, refetch } = useApi(() => groupSessionService.getById(id), [id]);
  // ApiResponse.success wraps in res.data.data.session OR res.data.data directly
  const session = data?.data?.session ?? data?.session ?? (
    data?.data && !Array.isArray(data.data) ? data.data : null
  );

  if (loading) return (
    <DashboardLayout title="Group Session">
      <div className="max-w-2xl space-y-4 animate-pulse">
        {[1,2,3].map(i => <div key={i} className="h-32 bg-white border border-cream-200 rounded-2xl" />)}
      </div>
    </DashboardLayout>
  );

  if (error || !session) return (
    <DashboardLayout title="Group Session">
      <div className="max-w-2xl bg-white border border-cream-200 rounded-2xl p-10 text-center">
        <AlertCircle size={36} className="mx-auto text-rust-400 mb-3" />
        <p className="font-serif text-xl text-forest-900">Session not found</p>
      </div>
    </DashboardLayout>
  );

  const Icon     = SESSION_ICON[session.sessionType] ?? Video;
  const isHost   = session.hostUserId === user?.id;
  const isLive   = session.status === 'LIVE';
  const isSched  = session.status === 'SCHEDULED';
  const isActive = isLive || isSched;

  const date = session.scheduledAt
    ? new Date(session.scheduledAt).toLocaleString('en-GH', { dateStyle: 'long', timeStyle: 'short' })
    : '—';

  const handleCopy = () => {
    navigator.clipboard.writeText(session.joinCode);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    const text = `Join my TourEnt Hub group session — "${session.title}"\nCode: ${session.joinCode}\nDate: ${date}\n\nJoin at: tourenthub.vercel.app/group-sessions`;
    if (navigator.share) {
      navigator.share({ title: session.title, text });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Session info copied — paste and share!');
    }
  };

  const handleComplete = async () => {
    if (!window.confirm('Mark this group session as complete?')) return;
    setCompleting(true);
    try {
      await groupSessionService.complete(session.id);
      toast.success('Session marked as complete!');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not complete session');
    } finally { setCompleting(false); }
  };

  return (
    <DashboardLayout title="Group Session">
      <div className="max-w-2xl space-y-5">

        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-forest-500 hover:text-forest-900 transition-colors group">
          <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform" />
          Back to Group Sessions
        </button>

        {/* Header card */}
        <div className={`bg-white border-2 rounded-2xl p-6 space-y-4 ${isLive ? 'border-green-300' : 'border-cream-200'}`}>
          {isLive && (
            <div className="flex items-center gap-2 text-green-600">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-widest">Session is Live</span>
            </div>
          )}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-forest-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Icon size={20} className="text-forest-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h2 className="font-serif text-xl font-semibold text-forest-900">{session.title}</h2>
                <span className={`text-[10px] font-semibold border px-2.5 py-1 rounded-full flex-shrink-0 ${STATUS_STYLE[session.status]}`}>
                  {session.status}
                </span>
              </div>
              {session.description && (
                <p className="text-sm text-forest-500 mt-1 leading-relaxed">{session.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-forest-400 flex-wrap">
                <span className="flex items-center gap-1"><Calendar size={10} /> {date}</span>
                <span className="flex items-center gap-1"><Clock size={10} /> {session.duration} mins</span>
                <span className="flex items-center gap-1"><Users size={10} /> {session.participants?.length ?? 0}/{session.maxParticipants} joined</span>
              </div>
            </div>
          </div>

          {/* Join code — for sharing */}
          {isActive && (
            <div className="bg-forest-900 rounded-2xl p-5">
              <p className="text-xs text-forest-400 uppercase tracking-widest mb-2">Share this code to invite others</p>
              <div className="flex items-center gap-4">
                <span className="font-mono text-3xl font-bold text-cream-50 tracking-[0.2em]">{session.joinCode}</span>
                <div className="flex gap-2 ml-auto">
                  <button onClick={handleCopy}
                    className="flex items-center gap-1.5 text-xs bg-forest-700 hover:bg-forest-600 text-cream-50 px-3 py-2 rounded-xl transition-colors">
                    {copied ? <CheckCircle2 size={13} className="text-green-400" /> : <Copy size={13} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button onClick={handleShare}
                    className="flex items-center gap-1.5 text-xs bg-forest-700 hover:bg-forest-600 text-cream-50 px-3 py-2 rounded-xl transition-colors">
                    <Share2 size={13} /> Share
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-forest-500 mt-2">
                Only registered TourEnt Hub users can join. Max {session.maxParticipants} participants.
              </p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3 flex-wrap">
            {isActive && (
              <Link to={`/group-sessions/${session.id}/room`}
                className="btn-primary flex items-center gap-2">
                <Icon size={15} /> {isLive ? 'Join Session Now' : 'Enter Room'} →
              </Link>
            )}
            {isActive && isHost && (
              <button onClick={handleComplete} disabled={completing}
                className="flex items-center gap-2 text-sm font-medium border border-forest-200 text-forest-700 hover:bg-forest-50 px-4 py-2.5 rounded-full transition-colors disabled:opacity-60">
                {completing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Mark Complete
              </button>
            )}
          </div>
        </div>

        {/* Expert */}
        {session.expertProfile && (
          <div className="bg-white border border-cream-200 rounded-2xl p-5">
            <h4 className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-3">Expert</h4>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-sm">
                {session.expertProfile.user?.firstName?.[0] ?? 'E'}
              </div>
              <div>
                <p className="text-sm font-semibold text-forest-900">
                  {session.expertProfile.user?.firstName} {session.expertProfile.user?.lastName}
                </p>
                <p className="text-xs text-forest-400">Expert</p>
              </div>
            </div>
          </div>
        )}

        {/* Participants */}
        <div className="bg-white border border-cream-200 rounded-2xl p-5">
          <h4 className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-4">
            Participants ({session.participants?.length ?? 0}/{session.maxParticipants})
          </h4>
          <div className="space-y-3">
            {(session.participants ?? []).map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-9 h-9 bg-forest-600 rounded-xl flex items-center justify-center text-cream-50 font-serif font-bold text-xs overflow-hidden flex-shrink-0">
                  {p.user?.avatar
                    ? <img src={p.user.avatar} alt="" className="w-full h-full object-cover" />
                    : `${p.user?.firstName?.[0] ?? '?'}`}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-forest-900 truncate">
                    {p.user?.firstName} {p.user?.lastName}
                    {p.userId === user?.id && <span className="text-forest-400 font-normal"> (you)</span>}
                  </p>
                  <p className="text-[10px] text-forest-400 capitalize">{p.role.toLowerCase()}</p>
                </div>
                {p.joinedAt && (
                  <span className="text-[10px] text-green-600 bg-green-50 border border-green-100 px-2 py-0.5 rounded-full">Joined</span>
                )}
              </div>
            ))}
            {(session.participants ?? []).length === 0 && (
              <p className="text-xs text-forest-400 italic">No participants yet</p>
            )}
          </div>
          {isActive && (
            <p className="text-xs text-forest-400 mt-4 pt-3 border-t border-cream-100">
              Share code <strong className="text-forest-700 font-mono">{session.joinCode}</strong> to invite more people ({session.maxParticipants - (session.participants?.length ?? 0)} spots left)
            </p>
          )}
        </div>

        {/* Notes */}
        {session.notes && (
          <div className="bg-cream-50 border border-cream-200 rounded-2xl p-5">
            <h4 className="text-xs font-semibold text-forest-600 uppercase tracking-widest mb-2">Notes</h4>
            <p className="text-sm text-forest-700 leading-relaxed whitespace-pre-line">{session.notes}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GroupSessionDetailPage;